<template>
	<div
		class="c-step-by-step-video-item"
		:class="props.videoWrapperClass"
		:style="'--video-loop-aspect-ratio:' + (props.aspectRatio || 'auto')"
	>
		<iframe
			ref="video"
			class="c-step-by-step-video-item__iframe"
			:src="embedUrl"
			allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
			tabindex="-1"
			aria-hidden="true"
			title=""
		></iframe>

		<DsButtonPlayProgress
			ref="playBtn"
			class="c-step-by-step-video-item__progress-button"
			:aria-label-play="playAriaLabel"
			:aria-label-pause="pauseAriaLabel"
			initial-state="play"
			:progress="videoProgress"
			:playHandler="play"
			:pauseHandler="pause"
			aria-live="polite"
		/>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	watch,
	onMounted,
	onBeforeUnmount,
	computed,
	nextTick,
} from 'vue';
import Player from '@vimeo/player';
import { DsButtonPlayProgress } from '@coloplast/design-system';
import { youtubeVideoUrl } from 'shared/services/urlTypeService';
import { track_content_engagement } from 'shared/services/tracking/contentTrackingService';
import { ensureYouTubeAPI } from './youtube-api';

declare global {
	interface Window {
		marketingPermissions?: boolean;
		onYouTubeIframeAPIReady?: () => void;
	}
}

const props = defineProps<{
	videoUrl: string;
	isExpanded: boolean;
	videoId: string;
	videoTitle?: string;
	playAriaLabel?: string;
	pauseAriaLabel?: string;
	videoWrapperClass?: string;
	aspectRatio?: string;
	headline?: string;
}>();

type VimeoPlayer = Player;

let vimeoPlayer: VimeoPlayer;
let ytPlayer: YT.Player;

const video = ref<HTMLIFrameElement | null>(null);
const videoIsPlaying = ref(false);
const videoProgress = ref(0);
const videoTracked = ref(false);
const wasPlayingBeforeCollapse = ref(false);
const playerReady = ref(false);

type PlayButtonRef = { setButtonState: (isPlaying: boolean) => void } | null;
const playBtn = ref<PlayButtonRef>(null);

const isVimeo = computed(() => props.videoUrl.includes('vimeo.com'));

const parsedVideoId = computed(() => {
	const match = props.videoUrl.match(/watch\?v=([^&$]*)/);
	if (match) return match[1];
	const parts = props.videoUrl.split('/');
	const partAfterSlash = parts[parts.length - 1];
	return partAfterSlash.split('?')[0];
});

const embedUrl = computed(() => {
	if (isVimeo.value) {
		let url = `https://player.vimeo.com/video/${parsedVideoId.value}?autoplay=1&muted=1&controls=0&api=1&playsinline=1&loop=1&responsive=1&autopip=none&pip=none&title=0&byline=0&portrait=0&sidedock=0`;
		if (!window.marketingPermissions) url += '&dnt=true';
		return url;
	}
	return `${youtubeVideoUrl()}${
		parsedVideoId.value
	}?fs=0&cc_load_policy=0&controls=0&iv_load_policy=3&enablejsapi=1&version=3&playsinline=1&autoplay=1&mute=1&rel=0&playlist=${
		parsedVideoId.value
	}&loop=1`;
});

let progressInterval: ReturnType<typeof setInterval> | null = null;

function trackStart() {
	if (!videoTracked.value) {
		track_content_engagement(
			'video',
			'start',
			props.headline || 'no_data',
			props.videoUrl
		);
		videoTracked.value = true;
	}
}

async function initPlayer() {
	await nextTick();
	if (!video.value) return;

	if (isVimeo.value) {
		vimeoPlayer = new Player(video.value, {
			id: parseInt(parsedVideoId.value, 10),
		});
		await vimeoPlayer.ready();

		playerReady.value = true;

		vimeoPlayer.on('timeupdate', (e) => {
			videoProgress.value = e.percent;
		});

		vimeoPlayer.on('play', () => {
			if (!videoIsPlaying.value) trackStart();
			videoIsPlaying.value = true;
			playBtn.value?.setButtonState(true);
		});

		vimeoPlayer.on('pause', () => {
			videoIsPlaying.value = false;
			playBtn.value?.setButtonState(false);
		});

		vimeoPlayer.on('ended', () => {
			videoIsPlaying.value = false;
			playBtn.value?.setButtonState(false);
		});
	} else {
		await ensureYouTubeAPI();
		ytPlayer = new window.YT.Player(video.value, {
			events: {
				onReady: () => {
					playerReady.value = true;
					if (props.isExpanded) play();
				},
				onStateChange: (e) => {
					const { PLAYING, PAUSED, ENDED } = window.YT.PlayerState;
					if (e.data === PLAYING) {
						videoIsPlaying.value = true;
						playBtn.value?.setButtonState(true);
						startProgressTracking();
					} else if (e.data === PAUSED || e.data === ENDED) {
						videoIsPlaying.value = false;
						playBtn.value?.setButtonState(false);
						stopProgressTracking();
					}
				},
			},
		});
	}
}

async function play() {
	if (!vimeoPlayer && !ytPlayer) return;

	videoIsPlaying.value = true;
	trackStart();

	if (isVimeo.value) {
		vimeoPlayer.play();
	} else {
		ytPlayer.playVideo();
		startProgressTracking();
	}
}

function pause() {
	if (!vimeoPlayer && !ytPlayer) return;

	if (isVimeo.value) vimeoPlayer.pause();
	else ytPlayer.pauseVideo();

	videoIsPlaying.value = false;
	stopProgressTracking();
}

function startProgressTracking() {
	if (progressInterval) return;
	progressInterval = setInterval(updateProgress, 1000);
}

function stopProgressTracking() {
	if (progressInterval) {
		clearInterval(progressInterval);
		progressInterval = null;
	}
}

function updateProgress() {
	if (!isVimeo.value && ytPlayer?.getDuration) {
		try {
			const d = ytPlayer.getDuration();
			const t = ytPlayer.getCurrentTime();
			videoProgress.value = d > 0 ? t / d : 0;
		} catch {
			console.warn('Issue updating video progress for YouTube');
		}
	}
}

watch(
	() => props.isExpanded,
	(expanded) => {
		if (!playerReady.value) return;
		if (!expanded) {
			wasPlayingBeforeCollapse.value = videoIsPlaying.value;
			pause();
		} else if (wasPlayingBeforeCollapse.value) play();
	}
);

onMounted(async () => {
	playBtn.value?.setButtonState(false);
	await initPlayer();
});

onBeforeUnmount(() => {
	stopProgressTracking();
});
</script>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';
@import '@coloplast/design-system/dist/components/button/ds-button-play-progress';

$aspectRationvideoItemIframeWidth: calc(100% * 1.771); //16 : 9 (1.771 : 1)
$leftvideoItemIframePosition: -38.55%; // (77.1% / 2)

.c-step-by-step-video-item {
	position: relative;
	overflow: hidden;
	display: flex;
	align-items: flex-end;
	justify-content: flex-end;
	border-radius: 8px;
	margin-block-end: ds-space(9);
	aspect-ratio: 1;

	@media (max-width: ds-breakpoint('sm')) {
		margin-block-end: ds-space(8);

		&.c-video-item--non-fixed-height {
			height: auto;
		}
	}

	@media (min-width: ds-breakpoint('sm')) {
		overflow-x: hidden;
	}

	&.--loop {
		height: 100%;
		border-radius: 0;
		margin-block-end: 0;

		@media (max-width: ds-breakpoint('sm')) {
			height: 224px;
		}
	}

	&--non-fixed-height {
		height: auto;
	}

	&__iframe {
		height: 100%;
		position: absolute;
		top: 0;
		left: $leftvideoItemIframePosition;
		width: $aspectRationvideoItemIframeWidth;
		border: none;
	}

	&__videocta {
		margin-right: ds-space(8);
		margin-bottom: ds-space(8);
		position: unset;
		z-index: 10;

		&::after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			right: 0;
			left: 0;
			z-index: -1;
		}

		@media (max-width: ds-breakpoint('sm')) {
			margin-right: 10px;
			margin-bottom: 10px;
		}
	}

	@media (min-width: ds-breakpoint('md')) {
		margin-block-end: 0;
	}

	.ds-button-play-progress {
		margin: 0 ds-space(6) ds-space(6) 0;
	}
}
</style>
